import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '../../../redux/store';
import { StaffLite, StaffsSummary } from '../types/responses/StaffLite';

type PageIndex = number;

interface CursorPage<T> {
	items: T[];
}

export interface StaffState {
	page: number;
	rowsPerPage: number;
	cursors: Array<number | null>;
	pageCache: Record<PageIndex, CursorPage<StaffLite>>;
	nextCursorByPage: Record<PageIndex, number | null>;
	virtualCount: number;
	summary?: StaffsSummary | null;
}

const initialState: StaffState = {
	page: 0,
	rowsPerPage: 50,
	cursors: [null],
	pageCache: {},
	nextCursorByPage: {},
	virtualCount: 0,
	summary: null,
};

const staffSlice = createSlice({
	name: 'staff',
	initialState,
	reducers: {
		resetStaffState: () => initialState,

		resetStaffCursorPaging(state) {
			state.page = 0;
			state.cursors = [null];
			state.pageCache = {};
			state.nextCursorByPage = {};
			state.virtualCount = 0;
		},

		setStaffRowsPerPage(state, action: PayloadAction<number>) {
			state.rowsPerPage = action.payload;
		},

		setStaffPage(state, action: PayloadAction<number>) {
			state.page = action.payload;
		},

		setStaffPageData(
			state,
			action: PayloadAction<{ page: number; items: StaffLite[]; nextCursor?: number | null }>,
		) {
			const { page, items, nextCursor } = action.payload;
			state.pageCache[page] = { items };
			state.nextCursorByPage[page] = nextCursor ?? null;
			state.cursors[page + 1] = nextCursor ?? null;

			const maxLoaded = Math.max(0, ...Object.keys(state.pageCache).map(n => +n));
			const hasNext = state.nextCursorByPage[maxLoaded] != null;
			state.virtualCount = (maxLoaded + 1) * state.rowsPerPage + (hasNext ? 1 : 0);
		},

		setStaffSummary(state, action: PayloadAction<StaffsSummary | null | undefined>) {
			state.summary = action.payload ?? null;
		},
	},
});

export const {
	resetStaffState,
	resetStaffCursorPaging,
	setStaffRowsPerPage,
	setStaffPage,
	setStaffPageData,
	setStaffSummary,
} = staffSlice.actions;

export default staffSlice.reducer;

// selectors
export const selectStaffPage = (s: RootState) => s.staff.page;
export const selectStaffRowsPerPage = (s: RootState) => s.staff.rowsPerPage;
export const selectStaffSummary = (s: RootState) => s.staff.summary;
export const selectStaffItems = (s: RootState) => s.staff.pageCache[s.staff.page]?.items ?? [];
export const selectStaffCount = (s: RootState) => s.staff.virtualCount;
export const selectStaffCursorForPage = (p: number) => (s: RootState) => s.staff.cursors[p] ?? null;
export const selectStaffHasCachedPage = (p: number) => (s: RootState) =>
	Boolean(s.staff.pageCache[p]);
